import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import {
  School as TrainingIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CompletedIcon,
  Event as CalendarIcon,
  Assignment as AssignedIcon,
  VolunteerActivism as SelfEnrolledIcon,
  EmojiEvents as TrophyIcon,
  LocalLibrary as CatalogIcon,
  MenuBook as CourseIcon,
  Add as EnrollIcon,
  Videocam as VideoCallIcon,
  RateReview as FeedbackIcon,
  VideoLibrary as RecordingsIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  Layers as SubCatIcon,
  FolderOpen as VersionIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import CoursePublicFeedbackPanel from './CoursePublicFeedbackPanel';
import { formatDateTime, formatHms } from './recordingDisplayUtils';

const PALETTE = [
  { bg: '#E8F4FC', border: '#4A90C8', accent: '#0F4C75', soft: '#F3F9FD' },
  { bg: '#EEF8F1', border: '#3D9B6A', accent: '#1B5E3B', soft: '#F4FBF6' },
  { bg: '#FFF4E5', border: '#E6A023', accent: '#8A5A00', soft: '#FFFAF2' },
  { bg: '#E6F7F5', border: '#2A9B91', accent: '#0D5C56', soft: '#F2FBFA' },
  { bg: '#FFE8EE', border: '#D45B7A', accent: '#8B2040', soft: '#FFF5F8' },
  { bg: '#F0EDE6', border: '#8A7A5C', accent: '#4A3F2A', soft: '#F8F6F1' },
];

const paletteFor = (key) => {
  const s = String(key || 'x');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const formatDuration = (minutes) => {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const editionLabelOf = (course) => {
  if (course?.edition_label) return course.edition_label;
  const ed = Number(course?.edition_number);
  if (ed >= 1 && ed <= 9999) return `V${String(ed).padStart(4, '0')}`;
  return 'Version';
};

const UserTraining = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [publishedCourses, setPublishedCourses] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [liveSessions, setLiveSessions] = useState({ live: [], upcoming: [] });
  const [recordings, setRecordings] = useState([]);
  const [playRecording, setPlayRecording] = useState(null);
  const [playUrl, setPlayUrl] = useState(null);
  const [mainTab, setMainTab] = useState(0);
  const [enrollFilter, setEnrollFilter] = useState('all');
  const [enrollingCat, setEnrollingCat] = useState(null);
  const [feedbackEnrollment, setFeedbackEnrollment] = useState(null);

  const fetchMyTraining = useCallback(async () => {
    try {
      const [response, liveRes, recRes, catalogRes] = await Promise.all([
        api.get('/training/user/my-training'),
        api.get('/training/user/live-sessions').catch(() => ({ data: { data: { live: [], upcoming: [] } } })),
        api.get('/training/user/live-recordings').catch(() => ({ data: { data: { recordings: [] } } })),
        api.get('/training/catalog').catch(() => ({ data: { data: [] } })),
      ]);
      const payload = response.data.data;
      if (Array.isArray(payload)) {
        setEnrollments(payload);
        setPublishedCourses([]);
      } else {
        setEnrollments(payload?.enrollments || []);
        setPublishedCourses(payload?.publishedCourses || []);
      }
      setLiveSessions(liveRes.data?.data || { live: [], upcoming: [] });
      setRecordings(recRes.data?.data?.recordings || []);
      setCatalog((catalogRes.data?.data || []).filter((c) => !c.isStandaloneExamCatalog));
    } catch (error) {
      console.error('Failed to fetch training:', error);
      toast.error('Failed to load your training');
    } finally {
      setLoading(false);
    }
  }, []);

  const enrollInCategory = async (categoryId, courseIds) => {
    if (!categoryId) return;
    setEnrollingCat(categoryId);
    try {
      const payload = { category_id: categoryId };
      if (Array.isArray(courseIds) && courseIds.length > 0) {
        payload.training_title_ids = courseIds;
      }
      await api.post('/training/user/subscribe', payload);
      toast.success('You are enrolled in this program');
      await fetchMyTraining();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not enroll');
    } finally {
      setEnrollingCat(null);
    }
  };

  useEffect(() => {
    fetchMyTraining();
  }, [fetchMyTraining]);

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    const open = async () => {
      if (!playRecording?.id) {
        setPlayUrl(null);
        return;
      }
      try {
        const path = playRecording.play_path || `/training/live-recordings/${playRecording.id}/stream`;
        const res = await api.get(path, { responseType: 'blob' });
        if (!active) return;
        objectUrl = URL.createObjectURL(res.data);
        setPlayUrl(objectUrl);
      } catch (e) {
        if (!active) return;
        toast.error(e.response?.data?.message || 'Could not play recording');
        setPlayRecording(null);
        setPlayUrl(null);
      }
    };
    open();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [playRecording]);

  const courseById = useMemo(() => {
    const map = new Map();
    (publishedCourses || []).forEach((c) => map.set(String(c.id), c));
    return map;
  }, [publishedCourses]);

  const recordingsByTitle = useMemo(() => {
    const map = new Map();
    (recordings || []).forEach((r) => {
      const tid = String(r.training_title_id || r.title?.id || '');
      if (!tid) return;
      if (!map.has(tid)) map.set(tid, []);
      map.get(tid).push(r);
    });
    return map;
  }, [recordings]);

  /** category → subcategory → course → version → recordings[] */
  const recordingsTree = useMemo(() => {
    const cats = new Map();
    (recordings || []).forEach((rec) => {
      const course = courseById.get(String(rec.training_title_id)) || rec.title || {};
      const cat = rec.category || course.category || { id: 'unknown', name: 'Programme' };
      const sub =
        rec.subCategory ||
        course.subCategory ||
        (rec.title?.learningSpot
          ? { id: rec.title.learningSpot.id, name: rec.title.learningSpot.name }
          : { id: 'general', name: 'General' });
      const catId = String(cat.id || 'unknown');
      const subId = String(sub.id || 'general');
      const courseName = course.name || rec.title?.name || 'Course';
      const courseKey = courseName.trim().toLowerCase();
      const edition = course.edition_label || rec.edition_label || editionLabelOf(course);

      if (!cats.has(catId)) {
        cats.set(catId, { category: cat, subcategories: new Map() });
      }
      const catNode = cats.get(catId);
      if (!catNode.subcategories.has(subId)) {
        catNode.subcategories.set(subId, { subCategory: sub, courses: new Map() });
      }
      const subNode = catNode.subcategories.get(subId);
      if (!subNode.courses.has(courseKey)) {
        subNode.courses.set(courseKey, { name: courseName, versions: new Map() });
      }
      const courseNode = subNode.courses.get(courseKey);
      const verKey = String(course.id || rec.training_title_id || edition);
      if (!courseNode.versions.has(verKey)) {
        courseNode.versions.set(verKey, {
          edition,
          training_title_id: course.id || rec.training_title_id,
          recordings: [],
        });
      }
      courseNode.versions.get(verKey).recordings.push(rec);
    });

    return Array.from(cats.values()).map((catNode) => ({
      ...catNode,
      subcategories: Array.from(catNode.subcategories.values()).map((subNode) => ({
        ...subNode,
        courses: Array.from(subNode.courses.values()).map((courseNode) => ({
          name: courseNode.name,
          versions: Array.from(courseNode.versions.values()),
        })),
      })),
    }));
  }, [recordings, courseById]);

  /** category → subcategory → courseName → versions[] */
  const enrolledTree = useMemo(() => {
    const cats = new Map();

    (enrollments || []).forEach((enrollment) => {
      const cat = enrollment.category || { id: enrollment.category_id, name: 'Program' };
      const catId = String(cat.id || enrollment.category_id);
      if (!cats.has(catId)) {
        cats.set(catId, {
          category: cat,
          enrollment,
          subcategories: new Map(),
        });
      }
      const catNode = cats.get(catId);
      const selected = new Set(
        (enrollment.selected_training_title_ids || []).map((id) => String(id))
      );

      let titles = (publishedCourses || []).filter((c) => selected.has(String(c.id)));
      if (!titles.length) {
        titles = (publishedCourses || []).filter((c) => String(c.category?.id) === catId);
      }

      titles.forEach((course) => {
        const sub = course.subCategory || { id: 'general', name: 'General' };
        const subId = String(sub.id);
        if (!catNode.subcategories.has(subId)) {
          catNode.subcategories.set(subId, {
            subCategory: sub,
            courses: new Map(),
          });
        }
        const subNode = catNode.subcategories.get(subId);
        const courseKey = String(course.name || 'Course').trim().toLowerCase();
        if (!subNode.courses.has(courseKey)) {
          subNode.courses.set(courseKey, {
            name: course.name || 'Course',
            versions: [],
          });
        }
        subNode.courses.get(courseKey).versions.push(course);
      });
    });

    return Array.from(cats.values()).map((catNode) => ({
      ...catNode,
      subcategories: Array.from(catNode.subcategories.values()).map((subNode) => ({
        ...subNode,
        courses: Array.from(subNode.courses.values()).map((courseNode) => ({
          ...courseNode,
          versions: courseNode.versions.sort(
            (a, b) => Number(b.edition_number || 0) - Number(a.edition_number || 0)
          ),
        })),
      })),
    }));
  }, [enrollments, publishedCourses]);

  const filteredEnrolledTree = useMemo(() => {
    if (enrollFilter === 'all') return enrolledTree;
    return enrolledTree.filter((node) => {
      const st = node.enrollment?.status;
      if (enrollFilter === 'completed') return st === 'completed';
      return st === 'enrolled' || st === 'in_progress';
    });
  }, [enrolledTree, enrollFilter]);

  const browseTree = useMemo(() => {
    const enrolledCatIds = new Set((enrollments || []).map((e) => String(e.category_id)));
    return (catalog || []).map((cat, idx) => {
      const palette = paletteFor(cat.id || idx);
      const enrolled = enrolledCatIds.has(String(cat.id));
      const courses = cat.publishedCourses || [];
      const bySub = new Map();
      courses.forEach((c) => {
        const subName = c.subCategory?.name || c.subCategoryName || c.learning_spot_name || 'General';
        const subId = String(c.subCategory?.id || c.subCategoryId || c.learning_spot_id || subName);
        if (!bySub.has(subId)) bySub.set(subId, { id: subId, name: subName, courses: new Map() });
        const sub = bySub.get(subId);
        const courseKey = String(c.name || 'Course').trim().toLowerCase();
        if (!sub.courses.has(courseKey)) {
          sub.courses.set(courseKey, { name: c.name || 'Course', versions: [] });
        }
        sub.courses.get(courseKey).versions.push(c);
      });
      return {
        category: cat,
        enrolled,
        enrollment: cat.enrollment,
        palette,
        subcategories: Array.from(bySub.values()).map((s) => ({
          ...s,
          courses: Array.from(s.courses.values()).map((cn) => ({
            ...cn,
            versions: cn.versions.sort(
              (a, b) => Number(b.edition_number || 0) - Number(a.edition_number || 0)
            ),
          })),
        })),
      };
    });
  }, [catalog, enrollments]);

  const stats = {
    total: enrollments.length,
    inProgress: enrollments.filter((e) => e.status === 'in_progress' || e.status === 'enrolled')
      .length,
    completed: enrollments.filter((e) => e.status === 'completed').length,
    totalHours: enrollments.reduce((sum, e) => {
      const hours = e.progress?.reduce((s, p) => s + (p.time_spent_minutes || 0), 0) || 0;
      return sum + hours;
    }, 0),
    recordings: recordings.length,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderVersionRow = (course, enrollment) => {
    const recs = recordingsByTitle.get(String(course.id)) || [];
    const ed = editionLabelOf(course);
    return (
      <Box
        key={course.id}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          py: 1.25,
          px: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
          mb: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              icon={<VersionIcon sx={{ fontSize: 16 }} />}
              label={ed}
              sx={{ fontWeight: 700, bgcolor: '#EEF2F6' }}
            />
            <Chip
              size="small"
              label={course.delivery_type === 'ILT' ? 'Instructor-led' : 'Self-paced'}
              variant="outlined"
            />
            {course.start_date && (
              <Chip
                size="small"
                icon={<CalendarIcon />}
                label={`Starts ${formatDate(course.start_date)}`}
                variant="outlined"
              />
            )}
            {recs.length > 0 && (
              <Chip
                size="small"
                color="secondary"
                icon={<RecordingsIcon />}
                label={`${recs.length} recording${recs.length === 1 ? '' : 's'}`}
              />
            )}
          </Stack>
          {course.course_code && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, fontFamily: 'ui-monospace, monospace' }}
            >
              {course.course_code}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0}>
          {enrollment && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={() => navigate(`/training/learn/${enrollment.id}`)}
              sx={{ bgcolor: '#0F4C75', '&:hover': { bgcolor: '#0a3552' } }}
            >
              Open
            </Button>
          )}
          {course.category?.id && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/training/catalog/category/${course.category.id}`)}
            >
              Program
            </Button>
          )}
        </Stack>
      </Box>
    );
  };

  const renderEnrolledTree = () => {
    if (!filteredEnrolledTree.length) {
      return (
        <Box
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: '#FAFBFC',
          }}
        >
          <TrainingIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No enrolled courses yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Browse the catalogue and enroll in a program to see category → subcategory → courses
            here.
          </Typography>
          <Button variant="contained" startIcon={<CatalogIcon />} onClick={() => setMainTab(1)}>
            Browse catalogue
          </Button>
        </Box>
      );
    }

    return filteredEnrolledTree.map((node) => {
      const cat = node.category || {};
      const enrollment = node.enrollment;
      const palette = paletteFor(cat.id || cat.name);
      const progressItems = enrollment?.progress || [];
      const totalItems = progressItems.length;
      const completedItems = progressItems.filter((p) => p.status === 'completed').length;
      const isCompleted = enrollment?.status === 'completed';

      return (
        <Accordion
          key={cat.id || enrollment?.id}
          defaultExpanded
          disableGutters
          sx={{
            mb: 2,
            border: `2px solid ${palette.border}`,
            borderRadius: '10px !important',
            bgcolor: palette.soft,
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
              <Box
                sx={{
                  width: 10,
                  alignSelf: 'stretch',
                  borderRadius: 1,
                  bgcolor: palette.border,
                  minHeight: 36,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: palette.accent }}>
                  <CategoryIcon sx={{ fontSize: 20, mr: 0.75, verticalAlign: 'text-bottom' }} />
                  {cat.name || 'Program'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    icon={
                      enrollment?.enrollment_type === 'assigned' ? (
                        <AssignedIcon />
                      ) : (
                        <SelfEnrolledIcon />
                      )
                    }
                    label={
                      enrollment?.enrollment_type === 'assigned' ? 'Assigned' : 'Self-enrolled'
                    }
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    color={isCompleted ? 'success' : 'primary'}
                    label={isCompleted ? 'Completed' : enrollment?.status || 'enrolled'}
                  />
                  <Chip
                    size="small"
                    label={`${completedItems}/${totalItems || '—'} items · ${enrollment?.progress_percentage || 0}%`}
                  />
                </Stack>
              </Box>
              {isCompleted && <TrophyIcon sx={{ color: 'success.main' }} />}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={Number(enrollment?.progress_percentage) || 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#fff',
                  border: `1px solid ${palette.border}`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: isCompleted ? '#3D9B6A' : palette.border,
                  },
                }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={isCompleted ? <CompletedIcon /> : <PlayIcon />}
                  onClick={() => navigate(`/training/learn/${enrollment.id}`)}
                  sx={{ bgcolor: palette.accent, '&:hover': { bgcolor: palette.accent } }}
                >
                  {isCompleted ? 'Review program' : 'Continue learning'}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<FeedbackIcon />}
                  onClick={() => setFeedbackEnrollment(enrollment)}
                >
                  Course feedback
                </Button>
              </Stack>
            </Box>

            {!node.subcategories.length ? (
              <Alert severity="info">No course versions linked to this enrollment yet.</Alert>
            ) : (
              node.subcategories.map((sub) => (
                <Box
                  key={sub.subCategory.id}
                  sx={{
                    mb: 2,
                    pl: 1.5,
                    borderLeft: `3px solid ${palette.border}`,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: palette.accent, mb: 1 }}
                  >
                    <SubCatIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    {sub.subCategory.name}
                  </Typography>
                  {sub.courses.map((courseNode) => (
                    <Box
                      key={courseNode.name}
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1px solid ${palette.border}`,
                        bgcolor: palette.bg,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        <CourseIcon
                          sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }}
                        />
                        {courseNode.name}
                        <Chip
                          size="small"
                          label={`${courseNode.versions.length} version${
                            courseNode.versions.length === 1 ? '' : 's'
                          }`}
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      {courseNode.versions.map((v) => renderVersionRow(v, enrollment))}
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  const renderBrowseTree = () => {
    if (!browseTree.length) {
      return (
        <Alert severity="info">No published programmes in the catalogue yet.</Alert>
      );
    }
    return browseTree.map((node) => {
      const { category: cat, palette, enrolled } = node;
      const courseIds = (cat.publishedCourses || []).map((c) => c.id);
      return (
        <Accordion
          key={cat.id}
          defaultExpanded={false}
          disableGutters
          sx={{
            mb: 2,
            border: `2px solid ${palette.border}`,
            borderRadius: '10px !important',
            bgcolor: palette.soft,
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
              <Box
                sx={{
                  width: 10,
                  alignSelf: 'stretch',
                  borderRadius: 1,
                  bgcolor: palette.border,
                  minHeight: 36,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: palette.accent }}>
                  {cat.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(cat.publishedCourses || []).length} published course version(s)
                  {cat.description ? ` · ${cat.description}` : ''}
                </Typography>
              </Box>
              {enrolled ? (
                <Chip size="small" color="success" label="Enrolled" />
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EnrollIcon />}
                  disabled={enrollingCat === cat.id || !courseIds.length}
                  onClick={(e) => {
                    e.stopPropagation();
                    enrollInCategory(cat.id, courseIds);
                  }}
                  sx={{ bgcolor: palette.accent }}
                >
                  {enrollingCat === cat.id ? 'Enrolling…' : 'Enroll'}
                </Button>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {!node.subcategories.length ? (
              <Typography variant="body2" color="text.secondary">
                No published courses in this programme.
              </Typography>
            ) : (
              node.subcategories.map((sub) => (
                <Box
                  key={sub.id}
                  sx={{ mb: 2, pl: 1.5, borderLeft: `3px solid ${palette.border}` }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: palette.accent }}>
                    {sub.name}
                  </Typography>
                  {sub.courses.map((courseNode) => (
                    <Box
                      key={courseNode.name}
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1px solid ${palette.border}`,
                        bgcolor: palette.bg,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        {courseNode.name}
                      </Typography>
                      {courseNode.versions.map((v) => renderVersionRow(v, null))}
                    </Box>
                  ))}
                </Box>
              ))
            )}
            <Button size="small" onClick={() => navigate(`/training/catalog/category/${cat.id}`)}>
              Open full catalogue page
            </Button>
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 750, display: 'flex', alignItems: 'center', gap: 1, color: '#0F4C75' }}
          >
            <TrainingIcon sx={{ fontSize: 32 }} />
            My Training
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enrolled programmes, catalogue browse, and session recordings in one place.
          </Typography>
        </Box>
      </Box>

      {(liveSessions.live?.length > 0) && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            border: '2px solid #D45B7A',
            bgcolor: '#FFF5F8',
          }}
        >
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <VideoCallIcon sx={{ color: '#D45B7A' }} />
            Live now
          </Typography>
          {(liveSessions.live || []).map((s) => (
            <Box
              key={s.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                gap: 2,
              }}
            >
              <Box>
                <Typography fontWeight={600}>{s.course_name || s.title?.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.contentItem?.label || 'Join Video Call'} · Live session
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<VideoCallIcon />}
                onClick={() => navigate(`/training/live/${s.id}`)}
                sx={{ bgcolor: '#D45B7A', '&:hover': { bgcolor: '#8B2040' } }}
              >
                Join
              </Button>
            </Box>
          ))}
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Programs', value: stats.total, color: '#0F4C75', bg: '#E8F4FC' },
          { label: 'In progress', value: stats.inProgress, color: '#8A5A00', bg: '#FFF4E5' },
          { label: 'Completed', value: stats.completed, color: '#1B5E3B', bg: '#EEF8F1' },
          { label: 'Time spent', value: formatDuration(stats.totalHours), color: '#0D5C56', bg: '#E6F7F5' },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: s.bg,
                border: `1px solid ${s.color}33`,
              }}
            >
              <Typography variant="caption" sx={{ color: s.color, fontWeight: 600 }}>
                {s.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 750, color: s.color }}>
                {s.value}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={mainTab}
          onChange={(e, v) => setMainTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<TrainingIcon />}
            iconPosition="start"
            label={`Enrolled courses (${stats.total})`}
          />
          <Tab icon={<CatalogIcon />} iconPosition="start" label="Browse catalogue" />
          <Tab
            icon={<RecordingsIcon />}
            iconPosition="start"
            label={`Recordings (${stats.recordings})`}
          />
        </Tabs>
      </Box>

      {mainTab === 0 && (
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {[
              { id: 'all', label: 'All' },
              { id: 'in_progress', label: 'In progress' },
              { id: 'completed', label: 'Completed' },
            ].map((f) => (
              <Chip
                key={f.id}
                label={f.label}
                color={enrollFilter === f.id ? 'primary' : 'default'}
                variant={enrollFilter === f.id ? 'filled' : 'outlined'}
                onClick={() => setEnrollFilter(f.id)}
              />
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Category → subcategory → course → versions. Categories are expanded by default.
          </Typography>
          {renderEnrolledTree()}
        </Box>
      )}

      {mainTab === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Browse published programmes. Expand a category to see subcategory → courses → versions.
          </Typography>
          {renderBrowseTree()}
        </Box>
      )}

      {mainTab === 2 && (
        <Box>
          {!recordings.length ? (
            <Box
              sx={{
                p: 5,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <RecordingsIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No session recordings yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
                Cloud recordings from live sessions you are enrolled in appear here after the
                meeting ends and the file is saved on the server.
              </Typography>
            </Box>
          ) : (
            recordingsTree.map((catNode) => {
              const palette = paletteFor(catNode.category?.id || catNode.category?.name);
              return (
                <Accordion
                  key={catNode.category?.id || catNode.category?.name}
                  defaultExpanded
                  disableGutters
                  sx={{
                    mb: 2,
                    border: `2px solid ${palette.border}`,
                    borderRadius: '10px !important',
                    bgcolor: palette.soft,
                    '&:before': { display: 'none' },
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: palette.accent }}>
                      <CategoryIcon sx={{ fontSize: 20, mr: 0.75, verticalAlign: 'text-bottom' }} />
                      {catNode.category?.name || 'Programme'}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {catNode.subcategories.map((sub) => (
                      <Box
                        key={sub.subCategory?.id || sub.subCategory?.name}
                        sx={{ mb: 2, pl: 1.5, borderLeft: `3px solid ${palette.border}` }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: palette.accent }}>
                          <SubCatIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
                          {sub.subCategory?.name || 'General'}
                        </Typography>
                        {sub.courses.map((courseNode) => (
                          <Box
                            key={courseNode.name}
                            sx={{
                              mb: 1.5,
                              p: 1.5,
                              borderRadius: 1.5,
                              border: `1px solid ${palette.border}`,
                              bgcolor: palette.bg,
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                              <CourseIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
                              {courseNode.name}
                            </Typography>
                            {courseNode.versions.map((ver) => (
                              <Box key={ver.training_title_id || ver.edition} sx={{ mb: 1.25 }}>
                                <Chip
                                  size="small"
                                  icon={<VersionIcon sx={{ fontSize: 16 }} />}
                                  label={ver.edition}
                                  sx={{ fontWeight: 700, mb: 1, bgcolor: '#fff' }}
                                />
                                <Stack spacing={1}>
                                  {ver.recordings.map((rec) => (
                                    <Box
                                      key={rec.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        p: 1.25,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: '#fff',
                                      }}
                                    >
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          sx={{ fontFamily: 'ui-monospace, monospace' }}
                                          noWrap
                                        >
                                          {rec.recording_label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Date {formatDateTime(rec.recording_started_at || rec.created_at)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Start {formatDateTime(rec.recording_started_at || rec.created_at)} · End{' '}
                                          {formatDateTime(rec.recording_ended_at)} · Duration{' '}
                                          {formatHms(rec.duration_seconds)}
                                        </Typography>
                                      </Box>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<PlayIcon />}
                                        onClick={() => setPlayRecording(rec)}
                                        sx={{ bgcolor: '#0F4C75', flexShrink: 0 }}
                                      >
                                        Play
                                      </Button>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      )}

      <Dialog
        open={!!feedbackEnrollment}
        onClose={() => setFeedbackEnrollment(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Course feedback — {feedbackEnrollment?.category?.name || 'Training'}
        </DialogTitle>
        <DialogContent dividers>
          {(Array.isArray(feedbackEnrollment?.selected_training_title_ids)
            ? feedbackEnrollment.selected_training_title_ids
            : []
          ).length === 0 ? (
            <Alert severity="info">
              No enrolled course titles found on this enrollment.
            </Alert>
          ) : (
            (feedbackEnrollment.selected_training_title_ids || []).map((titleId) => {
              const course = courseById.get(String(titleId));
              return (
                <Box key={titleId} sx={{ mb: 2 }}>
                  <CoursePublicFeedbackPanel
                    trainingTitleId={titleId}
                    courseName={course?.name || 'Course'}
                  />
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackEnrollment(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(playRecording)}
        onClose={() => {
          setPlayRecording(null);
          setPlayUrl(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{playRecording?.recording_label || 'Recording'}</DialogTitle>
        <DialogContent>
          {playRecording && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Start {formatDateTime(playRecording.recording_started_at || playRecording.created_at)} · End{' '}
              {formatDateTime(playRecording.recording_ended_at)} · Duration{' '}
              {formatHms(playRecording.duration_seconds)}
            </Typography>
          )}
          {playUrl ? (
            <Box
              component="video"
              src={playUrl}
              controls
              autoPlay
              sx={{ width: '100%', maxHeight: '80vh', bgcolor: '#000' }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPlayRecording(null);
              setPlayUrl(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserTraining;
